import React from 'react';
import { ICompareProduct, TRenderType } from 'base/interfaces/product-compare';
import ListProductImage from '@base/snippets/ListProduct/ListProductImage';
import ListProductName from '@base/snippets/ListProduct/ListProductName';
import CompareAddToCart from '@base/product/ProdCompare/CompareAddToCart';
import Tracker from '@base/lib/Tracker';
import { ITrackElementData } from 'base/utils/tracker';
import { useTrans } from 'base/utils/i18n';

declare type ProdAttrCellProps = {
    variantId: string;
    variantTrackCode: string;
    moduleId: string;
    trackIndex: number;
    product: ICompareProduct,
    attributeId: string;
    type: TRenderType;
    imageSize: number;
}

export const getProductAttrValue = (product: ICompareProduct, attributeId: string): React.ReactNode => {
    const attr = product.productAttributeList.find(item => item.attributeId === attributeId);
    return <>{attr?.attributeValues[0]?.name || ''}</>;
};

const ProdAttrCell: React.FC<ProdAttrCellProps> = props => {
    const {
        variantId,
        variantTrackCode,
        moduleId,
        trackIndex,
        product,
        attributeId,
        type,
        imageSize,
    } = props;
    const canJump = product.variantId !== variantId;
    const { t } = useTrans();

    const prodTrackCode = `${variantTrackCode}.${product.variantTrackCode || product.productTrackCode}`;
    const position = trackIndex + 1;

    const getTrackerData = (type?: string): Omit<ITrackElementData, 'action'> | undefined => {
        if (!canJump) {
            return void 0;
        }
        return {
            ...(type === 'add' ? null : product),
            targetType: 'variant',
            target: type ? `${prodTrackCode}.${type}` : prodTrackCode,
            relTarget: prodTrackCode,
            module: moduleId,
            position,
        }
    }

    switch (type) {
        case 'image':
            return (
                <Tracker trigger={'impr click'} data={getTrackerData()} disableProdAttributes>
                    <ListProductImage
                        product={product}
                        imageSize={imageSize}
                        canJump={canJump}
                        className={`js-compare-${product.variantId}`}
                    />
                </Tracker>
            );
        case 'title':
            return (
                <Tracker trigger={'click'} data={getTrackerData('title')}>
                    {canJump ? (
                        <ListProductName product={product} canJump={canJump}/>
                    ):(
                        <div className="name text-wrap">
                            <strong>{t('web_product_compare_this_item')}:</strong> {product.name}
                        </div>
                    )}
                </Tracker>
            );
        case 'option':
            return (
                <Tracker trigger={'click'} data={getTrackerData('add')}>
                    <CompareAddToCart product={product}/>
                </Tracker>
            )
        case 'text':
            return <div className="in text-wrap">{getProductAttrValue(product, attributeId)}</div>;
        default:
            break;
    }
    return null;
}

export default ProdAttrCell;
